/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IStoreSelectorStateManager } from '@msdyn365-commerce-modules/bopis-utilities';
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button, format } from '@msdyn365-commerce-modules/utilities';
import {
  AddToWishlistComponent,
  ICartlineResourceString,
  IWishlistActionSuccessResult
} from '@msdyn365-commerce/components';
import {
  IActionContext,
  ICoreContext,
  IGridSettings,
  IImageSettings,
  ITelemetry
} from '@msdyn365-commerce/core';
import { BaseCartState, getCartState,GlobalStateInput, ICartState } from '@msdyn365-commerce/global-state';
import {
  AttributeValue,
  CartLine,
  CommerceList,
  OrgUnitLocation,
  ProductAvailableQuantity,
  ProductDeliveryOptions,
  SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import * as React from 'react';
import SmweCartLine from '../../../components/elicit-cartlineitem.component';
import ProductType from '../../../Utilities/productType';
import {
  IElicitPickUpInStoreViewProps,
  PickUpInStore
} from './elicit-cart-pick-up-in-store';

export interface IElicitCartLineItemsProps {
  percentSaving: number;
  templateSavingText?: string;
  templatePercentSavingText?: string;
  cartlines: CartLine[];
  mixedCart: {[index:string]: boolean};
  mixedCartLines: CartLine[];
  cartLineErrors: {[index:string]: boolean};
  cartState: ICartState | undefined;
  orgUnitLocations: OrgUnitLocation[] | undefined;
  resources: ICartlineResourceString;
  productAvailabilites: ProductAvailableQuantity[] | undefined;
  products: SimpleProduct[] | undefined;
  productDeliveryOptions: ProductDeliveryOptions[] | undefined;
  pickupDeliveryModeCode?: string;
  /** GridSettings for the product image in cartline  */
  gridSettings: IGridSettings;
  /** ImageSettings for the product image in cartline  */
  imageSettings: IImageSettings;
  id: string;
  typeName: string;
  context: ICoreContext;
  telemetry: ITelemetry;
  removeButtonText: string;
  addToWishlistButtonText: string;
  removeFromWishlistButtonText: string;
  shipItText: string;
  pickitUpText: string;
  changeStoreText: string;
  storeSelectorStateManager: IStoreSelectorStateManager | undefined;
  outOfStockThreshold: number;
  isStockCheckEnabled: boolean;
  wishlists: CommerceList[] | undefined;
  defaultWishlistName: string;
  maxCartlineQuantity: number;
  showImages: boolean | undefined;
  invalidProductError: string;
  outOfStockError: string;
  zeroAvailabilityError: string;
  showDiscountPercent?: boolean;
  productAttributesList?: { [key: number]: AttributeValue[] };
  removeItemClickHandler(cartlineToRemove: CartLine): void;
  moveToWishlistSuccessHandler(
    result: IWishlistActionSuccessResult,
    cartlineId: CartLine
  ): void;
  updateCartLinesQuantitySuccessHandler(
    cartline: CartLine,
    quantity: number
  ): void;
}

export interface IElicitCartlinesViewProps {
  cartline: React.ReactNode;
  pickUpInStore?: IElicitPickUpInStoreViewProps;
  remove: React.ReactNode;
  addToWishlist: React.ReactNode | undefined;
}

const _getProduct = (
  productId: number | undefined,
  products: SimpleProduct[]
): SimpleProduct | undefined => {
  if (productId !== undefined && products) {
    return products.find(product => {
      return productId === product.RecordId;
    });
  }
  return undefined;
};

const _getCartItemMaxQuantity = (
  outOfStockThreshold: number,
  maxCartlineQuantity: number,
  isStockCheckEnabled: boolean,
  cartLines: CartLine[],
  currentQuantity: number,
  productId: number | undefined,
  productAvailability?: ProductAvailableQuantity
) => {
  if (isStockCheckEnabled) {
    if (
      !productAvailability ||
      !productAvailability.AvailableQuantity ||
      productAvailability.AvailableQuantity <= 0
    ) {
      return 0;
    }
    let productInCartQty = 0;
    const productInCart = cartLines.filter(cartline => {
      return cartline.ProductId === productId;
    });
    if (productInCart && productInCart.length) {
      productInCart.forEach(line => {
        productInCartQty = productInCartQty + line.Quantity!;
      });
      productInCartQty = productInCartQty - currentQuantity;
      if (productInCartQty >= (productAvailability.AvailableQuantity)) {
        return currentQuantity;
      }
    }
    return productAvailability.AvailableQuantity - productInCartQty;
  }

  return maxCartlineQuantity;
};

const _updateCartLine = (ctx: IActionContext, cartState: ICartState,  cartLine: CartLine): void => {
  if (ctx && cartState) {
    updateCartLinesAsync({ callerContext: ctx }, cartState.cart.Id, [cartLine])
      .then(cart => {
              const cartInput = new GlobalStateInput<ICartState>(
                  'CARTSTATE',
                  BaseCartState,
                  ctx.requestContext.apiSettings);
        getCartState(ctx)
          .then(newCart => {
                  ctx.update(cartInput, newCart);
                  // tslint:disable-next-line: no-floating-promises
                  void newCart.refreshCart({});
          })
          .catch((e) => ctx.telemetry.exception(e));
      })
      .catch((e) => ctx.telemetry.exception(e));
  }
};

// tslint:disable:max-func-body-length
const _assembleNode = (
  cartline: CartLine,
  product: SimpleProduct,
  props: IElicitCartLineItemsProps,
  index: number,
  zeroAvailabilityError: boolean,
  cartState: ICartState | undefined,
  foundProductAvailability?: ProductAvailableQuantity,
  foundProductDeliveryOptions?: ProductDeliveryOptions,
): IElicitCartlinesViewProps => {
  const {
    imageSettings,
    gridSettings,
    id,
    typeName,
    context,
    resources,
    removeButtonText,
    removeItemClickHandler,
    moveToWishlistSuccessHandler,
    addToWishlistButtonText,
    removeFromWishlistButtonText,
    wishlists,
    defaultWishlistName,
    cartLineErrors,
    showDiscountPercent
  } = props;

  const maxQuantity = _getCartItemMaxQuantity(
    props.outOfStockThreshold,
    props.maxCartlineQuantity,
    props.isStockCheckEnabled,
    props.mixedCartLines,
    cartline.Quantity || 0,
    product.RecordId,
    foundProductAvailability
  );

  const isAuthenticated = context.request.user.isAuthenticated;
  const nameOfWishlist =
    wishlists && wishlists.length > 0 && wishlists[0].Name
      ? wishlists[0].Name
      : defaultWishlistName;

  const onRemoveClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    removeItemClickHandler(cartline);
  };

  return {
    cartline: (
      <SmweCartLine
        templatePercentSavingText={props.templatePercentSavingText!}
        templateSavingText={props.templateSavingText!}
        percentSaving={props.percentSaving}
        data={{
          cartLine: cartline,
          product: product
        }}
        currentQuantity={cartline.Quantity}
        maxQuantity={maxQuantity}
        isOutOfStock={
          product.ItemId?.toLowerCase() === props.context.request.channel?.GiftCardItemId?.toLowerCase() ? false :
            props.isStockCheckEnabled ? (maxQuantity <= 0 ? true : false) : false
        }
        gridSettings={gridSettings}
        imageSettings={imageSettings}
        id={id}
        typeName={typeName}
        productUrl={getProductUrlSync(
          product,
          props.context.actionContext,
          undefined
        )}
        context={context}
        resources={resources}
        key={index}
        isQuantityEditable={true}
        quantityOnChange={props.updateCartLinesQuantitySuccessHandler}
        primaryImageUrl={product.PrimaryImageUrl}
        remove={
          <Button
            className='msc-cart-line__remove-item'
            onClick={onRemoveClickHandler}
            title={removeButtonText}
          />
        }
        showImages={props.showImages}
        zeroAvailabilityError={zeroAvailabilityError}
        cartLineOnChange={_updateCartLine}
        cartState={cartState}
        cartLineErrors={cartLineErrors}
        showDiscountPercent={showDiscountPercent}
        productAttributes={props.productAttributesList && props.productAttributesList[product.RecordId]}
      />
    ),
    pickUpInStore: PickUpInStore({
      cartState: props.cartState,
      cartline: cartline,
      product: product,
      shipitText: props.shipItText,
      pickUpInStoreText: props.pickitUpText,
      changeStoreText: props.changeStoreText,
      storeSelectorStateManager: props.storeSelectorStateManager,
      orgUnitLocations: props.orgUnitLocations,
      deliveryOptions: foundProductDeliveryOptions,
      pickupDeliveryModeCode: props.pickupDeliveryModeCode
    }),
    remove: (
      <Button
        className='msc-cart-line__remove-item'
        onClick={onRemoveClickHandler}
        title={removeButtonText}
      >
        {removeButtonText}
      </Button>
    ),
    addToWishlist: isAuthenticated ? (
      <AddToWishlistComponent
        className={'msc-cart-line__add-to-wishlist'}
        addToWishlistButtonText={addToWishlistButtonText}
        removeFromWishlistButtonText={removeFromWishlistButtonText}
        context={context}
        id={id}
        key={cartline.LineId}
        typeName={typeName}
        nameOfWishlist={nameOfWishlist}
        cartline={cartline}
        showButtonText={true}
        showStatusMessage={false}
        showRemoveButton={false}
        showButtonTooltip={false}
        data={{
          wishlists: wishlists,
          product: product
        }}
        onSuccess={moveToWishlistSuccessHandler}
      />
    ) : (
      undefined
    )
  };
};

export const _assembleCartlines = (
  cartlines: CartLine[],
  products: SimpleProduct[] | undefined,
  props: IElicitCartLineItemsProps,
  cartState: ICartState | undefined
): IElicitCartlinesViewProps[] | null => {
  const reactNodes: IElicitCartlinesViewProps[] = [];

  if (!products || products.length === 0) {
    return null;
  }

  const productTypeInit = new ProductType();

  cartlines.map(cartLine => {
    productTypeInit.check(cartLine?.DeliveryMode, props.context);
  });
  props.mixedCart.isMixedCart = productTypeInit.isMixedProduct();

  cartlines.map((cartline, index) => {
    const product = _getProduct(cartline.ProductId, products);
    let foundProductAvailability;
    if (props.productAvailabilites && props.productAvailabilites.length > 0) {
      foundProductAvailability = props.productAvailabilites.find(
        productAvailability => {
          return productAvailability.ProductId! === cartline.ProductId;
        }
      );
    }
    let foundProductDeliveryOption;
    if (
      props.productDeliveryOptions &&
      props.productDeliveryOptions.length > 0
    ) {
      foundProductDeliveryOption = props.productDeliveryOptions.find(
        deliveryOption => {
          return (
            deliveryOption && deliveryOption.ProductId === cartline.ProductId
          );
        }
      );
    }

    const maxQuantity = _getCartItemMaxQuantity(
      props.outOfStockThreshold,
      props.maxCartlineQuantity,
      props.isStockCheckEnabled,
      props.mixedCartLines,
      cartline.Quantity || 0,
      product?.RecordId,
      foundProductAvailability
    );

    let zeroAvailabilityError: boolean = false;
    const productType = new ProductType();
    productType.check(cartline?.DeliveryMode, props.context);
    if (productType.isWineProduct() && (cartline.Quantity) && foundProductAvailability && foundProductAvailability.AvailableQuantity && foundProductAvailability.AvailableQuantity <= props.outOfStockThreshold) {
      cartline.Comment = props.zeroAvailabilityError;
      props.cartLineErrors[cartline.LineId!] = true;
      zeroAvailabilityError = true;
    } else if (productType.isWineProduct() && (cartline.Quantity && maxQuantity < cartline.Quantity)) {
      cartline.Comment = format(props.outOfStockError, maxQuantity);
      props.cartLineErrors[cartline.LineId!] = true;
    } else if (cartline.ItemId?.toLowerCase() !== props.context.request.channel?.GiftCardItemId?.toLowerCase()) {
      cartline.Comment = '';
      props.cartLineErrors[cartline.LineId!] = false;
    }

    if (product) {
      reactNodes.push(
        _assembleNode(
          cartline,
          product,
          props,
          index,
          zeroAvailabilityError,
          cartState,
          foundProductAvailability,
          foundProductDeliveryOption
        )
      );
    }
  });

  return reactNodes;
};

/**
 * CartLineItems component
 */

export const ElicitCartLineItems = (props: IElicitCartLineItemsProps) => {
  const { products, cartlines, cartState } = props;
  return _assembleCartlines(cartlines, products, props, cartState);
};
